import { useMemo, useSyncExternalStore } from 'react';

/**
 * WHAT A TAB MUST REMEMBER ACROSS A RELOAD OR A STRIPE REDIRECT — and nothing else.
 *
 * Two facts, both identifiers only (never an amount, never a status):
 *
 *   - the credit purchase whose card step was just confirmed. A redirect-based
 *     payment method leaves the workspace and comes back to the same address,
 *     and the RETURN IS NOT PROOF OF PAYMENT (design B.4): only the purchase's
 *     own status is. So the uuid is kept before Stripe is called, and whatever
 *     page the producer comes back to asks the server until it knows;
 *   - the AI operations approved in this tab, so the post-run summary survives
 *     the screen that started them. Every AI entry point is swapped for the
 *     run's progress indicator once the run starts, which unmounts it; the
 *     summary therefore lives in a tray the workspace shell keeps mounted.
 *
 * sessionStorage, per tab, like the session token: closing the tab forgets
 * both, and the wallet and history on Plan & billing remain the record.
 */

const PURCHASE_PREFIX = 'sb.ai_credit_purchase.';
const OPERATIONS_PREFIX = 'sb.ai_operations.';

/** At most this many summaries are kept per production; the oldest is dropped first. */
const MAX_TRACKED = 3;

function read(key: string): string | null {
  try {
    return sessionStorage.getItem(key);
  } catch {
    // Private windows and blocked site data throw rather than returning null.
    return null;
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch {
    /* a tab that cannot remember still works; it just forgets on reload */
  }
}

const listeners = new Set<() => void>();

function changed(): void {
  for (const listener of [...listeners]) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

/* ---------------------------- the pending purchase ---------------------------- */

/** The credit purchase on this production whose payment is being confirmed, if any. */
export function pendingCreditPurchase(projectId: string): string | null {
  return read(PURCHASE_PREFIX + projectId);
}

export function rememberCreditPurchase(projectId: string, purchaseUuid: string): void {
  write(PURCHASE_PREFIX + projectId, purchaseUuid);
  changed();
}

/** Forget it once its status is final — paid, failed, cancelled or refunded. */
export function forgetCreditPurchase(projectId: string, purchaseUuid: string): void {
  if (read(PURCHASE_PREFIX + projectId) !== purchaseUuid) return;
  write(PURCHASE_PREFIX + projectId, null);
  changed();
}

export function usePendingCreditPurchase(projectId: string): string | null {
  return useSyncExternalStore(subscribe, () => pendingCreditPurchase(projectId), () => null);
}

/* ---------------------------- the tracked operations ---------------------------- */

/** One approved operation, with the screenplay version a continuation is estimated on. */
export interface TrackedAiOperation {
  operationId: string;
  scriptVersionId: string;
}

function isTracked(value: unknown): value is TrackedAiOperation {
  if (typeof value !== 'object' || value === null) return false;
  const entry = value as Record<string, unknown>;

  return typeof entry.operationId === 'string' && typeof entry.scriptVersionId === 'string';
}

/** The raw stored text, so `useSyncExternalStore` compares a string, not a fresh array. */
function trackedRaw(projectId: string): string {
  return read(OPERATIONS_PREFIX + projectId) ?? '[]';
}

function parseTracked(raw: string): TrackedAiOperation[] {
  try {
    const parsed: unknown = JSON.parse(raw);

    return Array.isArray(parsed)
      ? parsed.filter(isTracked).map((t) => ({ operationId: t.operationId, scriptVersionId: t.scriptVersionId }))
      : [];
  } catch {
    return [];
  }
}

export function trackedAiOperations(projectId: string): TrackedAiOperation[] {
  return parseTracked(trackedRaw(projectId));
}

export function trackAiOperation(projectId: string, operation: TrackedAiOperation): void {
  const rest = trackedAiOperations(projectId).filter((t) => t.operationId !== operation.operationId);
  write(OPERATIONS_PREFIX + projectId, JSON.stringify([...rest, operation].slice(-MAX_TRACKED)));
  changed();
}

export function untrackAiOperation(projectId: string, operationId: string): void {
  const rest = trackedAiOperations(projectId).filter((t) => t.operationId !== operationId);
  write(OPERATIONS_PREFIX + projectId, rest.length === 0 ? null : JSON.stringify(rest));
  changed();
}

export function useTrackedAiOperations(projectId: string): TrackedAiOperation[] {
  const raw = useSyncExternalStore(subscribe, () => trackedRaw(projectId), () => '[]');

  return useMemo(() => parseTracked(raw), [raw]);
}
